import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import OperationalAction from 'components/Operational/operationalAction';
import get from 'lodash/get';
import Button from 'components/Button';
import API from 'services/api';
import styles from './index.module.scss';
import { BASE_ASSET_CATEGORY, assetStatus } from 'utils/constants/assetText';
import { useTranslation } from 'react-i18next';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import { getAssetAssignee, getAssetReceiverData } from './helper';
import { getPersonName, getItemOrDefaultToValue } from 'utils/details';
import {
  userAssigneeListActions,
  assetPageSpecificActions,
  assetSelectors,
} from './index';
import { showSnackbar } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { Box, withStyles } from '@material-ui/core';
import { getUserRoles } from 'reducers/users';
import { ManagerAndAbove, userHasRole } from 'utils/helper/roles';
import Portal from 'portal/Portal';
import FormContainer from 'components/Forms';
import fieldSelection from 'components/Forms/Fields';
import Loader from 'components/Loader';
import { validationSchema } from 'containers/Pages/People/Personnel/Layouts/operational.jsx';
import Graphs from './graphs';
import TaskForm from 'containers/Pages/Tasks/CleanerTasks/form';
import EventsForm from 'containers/Pages/Events/Layouts/baseEventForm';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import { setSiteName } from 'utils/helper/site';
import { typeNameConstructor } from 'utils/DataTransform/Asset/AssetList';
import { getGlobalDepartmentRecords } from 'selectors/globalSelectors';
import { checkIfServicesHasIsTask } from 'utils/helper/product';
import { getSecurityCompanyServices } from 'reducers/securityCompany';
import { translateTwoValueSentence } from 'utils/methods/helpers';

const WithColor = withStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
  },
}))(Box);
const inputFields = [
  {
    field: 'passcode',
    type: 'text',
    required: true,
    placeholder: 'Please enter 4-6 digit passcode',
  },
];
const transformAssetForEvents = assetDetails => {
  return [
    {
      assetName: {
        value: getItemOrDefaultToValue(assetDetails, 'name'),
        type: 'text',
      },
      id: {
        value: getItemOrDefaultToValue(assetDetails, 'id'),
        type: 'text',
        display: 'none',
      },
      assetId: {
        label: 'Asset ID',
        value: getItemOrDefaultToValue(assetDetails, 'assetId'),
        type: 'text',
      },
      category: {
        value: getItemOrDefaultToValue(assetDetails.type, 'category'),
        type: 'text',
      },
      typeId: {
        value: getItemOrDefaultToValue(assetDetails.type, 'id'),
        type: 'text',
        display: 'none',
      },
      assetCategoryName: {
        value: getItemOrDefaultToValue(assetDetails.type, 'name'),
        type: 'text',
      },
      originalTypeName: {
        value: getItemOrDefaultToValue(assetDetails.type, 'name'),
        display: 'none',
      },
      typeName: {
        value: typeNameConstructor(
          assetDetails.type.name,
          assetDetails.type.assetTypeId,
        ),
        type: 'text',
      },
    },
  ];
};
const transformSiteForEvents = ({ assignedSite, siteList }) => {
  if (assignedSite && !assignedSite.id) return assignedSite;

  const requiredSite = siteList.find(f => f.id === assignedSite.id);

  return {
    id: requiredSite.id,
    siteName: setSiteName(requiredSite),
    name: requiredSite.name,
    siteId: requiredSite.siteId,
    bizId: requiredSite?.businessClient.id,
    businessClientId: requiredSite?.businessClient.businessClientId,
    businessClientName: requiredSite?.businessClient.businessClientName,
    boundary: {
      centreOfGravity: requiredSite.centreOfGravity,
      points: requiredSite.locationDetail[0].paths.map(p => ({
        latitude: p.lat,
        longitude: p.lng,
      })),
    },
  };
};

const OperationalComponent = () => {
  // selectors
  const assetDetail = useSelector(
    state => assetSelectors.getAssetDetailsRaw(state),
    shallowEqual,
  );
  const globalDepartments = useSelector(
    state => getGlobalDepartmentRecords(state),
    shallowEqual,
  );

  const personnel = useSelector(
    state => assetSelectors.getAssetAssigneeList(state),
    shallowEqual,
  );

  const services = useSelector(state => getSecurityCompanyServices(state));

  const assetGiver = useSelector(state => state.user, shallowEqual);
  //hooks
  const [otp, setOtp] = useState();
  const [assetAssignee, setAssetAssignee] = useState();
  const [openForm, setOpenForm] = useState(false);
  const [graphWindow, setGraphWindow] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assetUnavailablePopup, setAssetUnavailablePopup] = useState(false);
  const [assetReturningPopup, setAssetReturningPopup] = useState(false);
  const [assetHandoverPopup, setAssetHandoverPopup] = useState(false);
  const userRoles = useSelector(state => getUserRoles(state));
  const siteList = useSites();
  const { t } = useTranslation();

  const getDefaultValue = useCallback(() => {
    if (assetDetail.currentAssignee) {
      return {
        id: assetDetail.currentAssignee.general.id,
        label: getPersonName(assetDetail.currentAssignee.general),
      };
    } else {
      return null;
    }
  }, [assetDetail]);

  useEffect(() => {
    const defaultValue = getDefaultValue();
    setAssetAssignee(defaultValue);
  }, [getDefaultValue]);

  // actions--------------------
  const dispatch = useDispatch();
  const dispatchMakeAssetAvailableAction = useCallback(
    id => {
      dispatch(assetPageSpecificActions.makeAssetAvailable({ id }));
    },
    [dispatch],
  );
  const dispatchMarkAssetUnavailableAction = useCallback(
    id => {
      dispatch(assetPageSpecificActions.makeAssetUnavailable({ id }));
    },
    [dispatch],
  );
  const dispatchAssetHandoverAction = useCallback(
    id => {
      dispatch(assetPageSpecificActions.assetHandover(id));
    },
    [dispatch],
  );

  const dispatchRequestAssetReturnAction = useCallback(
    ({ assetAssigneeId, id, assetId, name, assignee }) => {
      dispatch(
        assetPageSpecificActions.requestAssetReturn({
          assetAssigneeId,
          id,
          assetId,
          name,
          assignee,
        }),
      );
    },
    [dispatch],
  );

  const dispatchFetchPersonnelAction = useCallback(() => {
    dispatch(userAssigneeListActions.fetchUserList());
  }, [dispatch]);

  useEffect(() => {
    dispatchFetchPersonnelAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detailId = get(assetDetail, 'id');
  useEffect(() => {
    setOtp(null);
  }, [detailId]);

  const sendOtp = async () => {
    try {
      const { data } = await API.generateOtp(detailId);
      setOtp(data.data.otp);
    } catch (err) {
      dispatch(
        showSnackbar({
          typeOfSnackbar: 'error',
          message: errorHandler(err),
        }),
      );
    }
  };

  const otpButton = otp => {
    if (
      get(assetDetail, 'type.category') !== BASE_ASSET_CATEGORY.PHONE.id &&
      get(assetDetail, 'type.category') !== BASE_ASSET_CATEGORY.SENSOR.id &&
      get(assetDetail, 'type.category') !== BASE_ASSET_CATEGORY.ROBOT.id
    ) {
      return null;
    }
    return (
      <div className={styles.phoneContainer}>
        {otp && (
          <WithColor className={styles.otp}>
            {t('Current')} OTP : <span>{otp}</span>
          </WithColor>
        )}
        {userHasRole({
          userRoles: userRoles,
          requiredRoles: ManagerAndAbove,
        }) && (
          <Button
            text={otp ? t('Regenerate OTP') : t('Generate OTP')}
            size="l"
            className={styles.otpButton}
            onClick={sendOtp}
          />
        )}
        <div className={styles.messageLabel}>
          *{t('Generate OTP to register the new phone.')}
        </div>
      </div>
    );
  };
  const assignBtnClickHandler = () => {
    checkAssetHandover(assetDetail.type.handoverRequired);
  };

  const checkAssetHandover = handoverRequire => {
    if (handoverRequire) {
      setAssetHandoverPopup(true);
    } else {
      Reflect.deleteProperty(assetGiver, 'location');
      const receiver = getAssetReceiverData(personnel, assetAssignee.id);
      handoverAssetToPersonnel({
        id: assetDetail.id,
        giver: assetGiver,
        receiver: receiver,
        state: assetStatus.DEPLOYING,
      });
    }
  };
  const markAssetToAvailable = id => {
    dispatchMakeAssetAvailableAction(id);
    setAssetReturningPopup(false);
  };
  const isAcknowledgeReturnDisable = () => {
    const isDisable =
      assetDetail.currentStatus === assetStatus.RETURNING &&
      assetDetail.type.handoverRequired;
    return !isDisable;
  };
  const checkAssetStatus = () => {
    if (assetDetail.currentStatus === assetStatus.IN_USE) {
      setAssetUnavailablePopup(true);
    } else if (assetDetail.currentStatus === assetStatus.UNAVAILABLE) {
      markAssetToAvailable(assetDetail.id);
    } else {
      dispatchMarkAssetUnavailableAction(assetDetail.id);
    }
  };
  const closeHandoverPopup = () => {
    setAssetHandoverPopup(false);
  };
  const handoverAssetToPersonnel = handoverData => {
    dispatchAssetHandoverAction({ ...handoverData });
    closeHandoverPopup();
  };

  const popupConfirmBtnHandler = () => {
    const receiver = getAssetReceiverData(personnel, assetAssignee.id);

    handoverAssetToPersonnel({
      id: assetDetail.id,
      giver: assetGiver,
      receiver: receiver,
      state: assetStatus.DEPLOYING,
    });
  };
  const unAssignBtnClickHandler = () => {
    Reflect.deleteProperty(assetGiver, 'location');
    unAssignAssetFromPersonnel({
      assetAssigneeId: assetAssignee.id,
      id: assetDetail.id,
      assetId: assetDetail.assetId,
      name: assetDetail.name,
      assignee: {
        firstName: assetDetail.currentAssignee.general.firstName,
        lastName: assetDetail.currentAssignee.general.lastName,
      },
    });
  };
  const unAssignAssetFromPersonnel = unAssignData => {
    dispatchRequestAssetReturnAction(unAssignData);
  };
  const clickAcknowledgeHandover = () => {
    setAssetReturningPopup(true);
  };
  const clickOpenGraph = () => {
    setGraphWindow(true);
  };
  const updatePassword = async vals => {
    setLoading(true);
    const data = { password: vals.passcode }; //required as API has different data key
    try {
      await API.updateAssetPasscode(assetDetail.id, data);
      setOpenForm(false);
    } catch (err) {
      console.log('err', err);
    }
    setLoading(false);
  };
  const resetButtonComponent = () => {
    if (
      get(assetDetail, 'type.category') !== BASE_ASSET_CATEGORY.SENSOR.id &&
      get(assetDetail, 'type.category') !== BASE_ASSET_CATEGORY.ROBOT.id
    ) {
      return null;
    }
    if (
      !userHasRole({
        userRoles: userRoles,
        requiredRoles: ManagerAndAbove,
      })
    ) {
      return null;
    }
    return (
      <Box
        component={'div'}
        display={'flex'}
        alignItems={'flex-start'}
        paddingLeft={'1rem'}
        paddingBottom={'1rem'}
        flexDirection={'column'}
      >
        <Button
          text="Reset Passcode"
          size="l"
          className={styles.otpButton}
          onClick={() => setOpenForm(true)}
        />

        {openForm && (
          <Portal id="portal">
            <div className={styles.relative}>
              <FormContainer
                size="s"
                title="Change Passcode"
                requireHeaderSaveBtn
                onClickClose={() => setOpenForm(false)}
                onSubmit={updatePassword}
                buttonText="Submit"
                validationSchema={validationSchema}
                initialValues={{}}
              >
                {props => {
                  return inputFields.map((item, key) =>
                    fieldSelection({ item, key, props }),
                  );
                }}
              </FormContainer>
              {loading && <Loader outline={true} />}
            </div>
          </Portal>
        )}
        {loading && <Loader outline={true} />}
      </Box>
    );
  };
  const getActionItem = type => {
    if (!assetDetail) {
      return null;
    }
    switch (type) {
      case 'assign':
        return {
          label: assetDetail.currentAssignee === null ? 'Assign' : 'Request',
          placeholder: 'Select Personnel',
          options: getAssetAssignee(personnel, assetDetail),
          selectedValue: assetAssignee,
          changeSelectedValue: setAssetAssignee,
          isDisabled:
            Boolean(assetDetail.currentAssignee) ||
            assetDetail.currentStatus === assetStatus.RETURNING,
          btnText: assetDetail.currentAssignee === null ? 'Assign' : 'Request',
          btnSize: 's',
          btnColor: 'primary',
          btnDisabled:
            !assetAssignee ||
            assetDetail.currentStatus === assetStatus.RETURNING,
          btnClick:
            assetDetail.currentAssignee === null
              ? assignBtnClickHandler
              : unAssignBtnClickHandler,
        };
      case 'change-status':
        return {
          label: 'Remove from use',
          status: assetDetail.currentStatus,
          onChangeHandler: checkAssetStatus,
        };
      case 'acknowledge-handover':
        return {
          btnText: 'Acknowledge Return',
          btnSize: 'xl',
          btnColor: 'primary',
          btnClickHandler: clickAcknowledgeHandover,
          btnDisabled: isAcknowledgeReturnDisable(),
          alignment: 'left',
        };
      case 'graph':
        return {
          btnText: 'Graphs',
          btnSize: 'xl',
          btnColor: 'primary',
          btnClickHandler: clickOpenGraph,
          alignment: 'left',
        };
      case 'tasks':
        return {
          btnText: 'Create task',
          btnSize: 'xl',
          btnColor: 'primary',
          btnClickHandler: setTasksOpen,
          alignment: 'left',
        };
      case 'events':
        return {
          btnText: 'Create Event',
          btnSize: 'xl',
          btnColor: 'primary',
          btnClickHandler: setEventsOpen,
          alignment: 'left',
        };
      default:
        return null;
    }
  };
  const markAssetUnavailable = id => {
    dispatchMarkAssetUnavailableAction(id);
    setAssetUnavailablePopup(false);
    setAssetHandoverPopup(false);
  };
  const cancelAssetAssignment = () => {
    markAssetUnavailable(assetDetail.id);
  };
  const changeStatusConfirmBtnHandler = () => {
    markAssetUnavailable(assetDetail.id);
  };
  const closeAssetUnavailablePopUp = () => {
    setAssetUnavailablePopup(false);
  };
  const confirmAssetAcknowledgeHandler = () => {
    markAssetToAvailable(assetDetail.id);
  };
  const cancelAssetAcknowledgeHandler = () => {
    markAssetUnavailable(assetDetail.id);
  };
  const closeAssetReturningPopUp = () => {
    setAssetReturningPopup(false);
  };

  const getPopupData = type => {
    if (!assetDetail) {
      return null;
    }
    switch (type) {
      case 'assign':
        return {
          showConfirmPopup: assetHandoverPopup,
          popupTitle: 'asset_handover_title',
          popupStatusMessage:
            assetDetail !== null
              ? translateTwoValueSentence('asset_handover_desc', {
                  value1:
                    assetDetail?.assetId &&
                    assetDetail?.assetId !== DEFAULT_EMPTY_VALUE
                      ? assetDetail.assetId
                      : '',
                  value2: assetDetail.name,
                })
              : '',

          confirmBtnHandler: popupConfirmBtnHandler,
          confirmBtnText: 'asset_handover_pos_button',
          cancelBtnHandler: cancelAssetAssignment,
          cancelBtnText: 'asset_handover_neg_button',
          closePopupHandler: closeHandoverPopup,
          isDisabled:
            assetDetail.currentAssignee !== null ||
            assetDetail.currentStatus === assetStatus.RETURNING,
          btnDisabled: assetDetail.currentStatus === assetStatus.RETURNING,
        };
      case 'change-status':
        return {
          showConfirmPopup: assetUnavailablePopup,
          popupTitle: 'asset_status_title',
          popupStatusMessage: translateTwoValueSentence('asset_status_desc', {
            value1:
              assetDetail?.assetId &&
              assetDetail?.assetId !== DEFAULT_EMPTY_VALUE
                ? assetDetail.assetId
                : '',
            value2: assetDetail.name,
          }),
          confirmBtnHandler: changeStatusConfirmBtnHandler,
          confirmBtnText: 'asset_status_pos_button',
          cancelBtnHandler: closeAssetUnavailablePopUp,
          cancelBtnText: 'asset_status_neg_button',
          closePopupHandler: closeAssetUnavailablePopUp,
        };
      case 'acknowledge-handover':
        return {
          showConfirmPopup: assetReturningPopup,
          popupTitle: 'asset_handover_title',
          popupStatusMessage: translateTwoValueSentence('asset_handover_desc', {
            value1:
              assetDetail?.assetId &&
              assetDetail?.assetId !== DEFAULT_EMPTY_VALUE
                ? assetDetail.assetId
                : '',
            value2: assetDetail?.name,
          }),
          confirmBtnHandler: confirmAssetAcknowledgeHandler,
          confirmBtnText: 'asset_handover_pos_button',
          cancelBtnHandler: cancelAssetAcknowledgeHandler,
          cancelBtnText: 'asset_handover_neg_button',
          closePopupHandler: closeAssetReturningPopUp,
        };
      default:
        return null;
    }
  };
  const assetDepartments = assetDetail?.departmentIds?.reduce((prev, curr) => {
    if (globalDepartments[curr]) {
      // if the department exists
      prev.push({ id: curr, name: globalDepartments[curr] });
    }
    return prev;
  }, []);
  return (
    <>
      <div className={styles.operationalPanel}>
        {get(assetDetail, 'type.category') ===
          BASE_ASSET_CATEGORY.SENSOR.id && (
          <OperationalAction type={'button'} item={getActionItem('graph')} />
        )}
        {checkIfServicesHasIsTask({ services }) && (
          <OperationalAction type={'button'} item={getActionItem('tasks')} />
        )}
        <OperationalAction type={'button'} item={getActionItem('events')} />
        <OperationalAction
          type={'toggle'}
          item={getActionItem('change-status')}
          confirmPopup={getPopupData('change-status')}
        />
        <OperationalAction
          type={'dropdown'}
          item={getActionItem('assign')}
          confirmPopup={getPopupData('assign')}
        />

        <div className={styles.actionButton}>
          <OperationalAction
            type={'button'}
            item={getActionItem('acknowledge-handover')}
            confirmPopup={getPopupData('acknowledge-handover')}
          />
        </div>
      </div>
      {resetButtonComponent()}
      {otpButton(otp)}
      {graphWindow && <Graphs setGraphWindow={setGraphWindow} />}

      {tasksOpen && (
        <Portal id={'portal'}>
          <TaskForm
            closeForm={() => setTasksOpen(prev => !prev)}
            size={'portal'}
            defaultFieldValues={{
              // make sure keys of this object should exactly match the field name of the component
              assetId: assetDetail,
              ...(assetDepartments && { departments: assetDepartments }),
              ...(assetDetail?.assignedSite?.id && {
                site: assetDetail.assignedSite,
              }),
            }}
            disablePresist={true}
            editMode={false}
            getAllTemplateTasks={true}
          />
        </Portal>
      )}
      {eventsOpen && (
        <Portal id={'portal'}>
          <EventsForm
            editMode={false}
            closeForm={() => setEventsOpen(prev => !prev)}
            size={'portal'}
            defaultFieldValues={{
              // make sure keys of this object should exactly match the field name of the component
              involvedAssets: transformAssetForEvents(assetDetail),
              ...(assetDetail?.assignedSite?.id && {
                // only add site if it is assigned
                site: transformSiteForEvents({
                  assignedSite: assetDetail.assignedSite,
                  siteList,
                }),
              }),
            }}
            disablePresist={true}
          />
        </Portal>
      )}
    </>
  );
};

export default OperationalComponent;
